import { Bot, Menu, Moon, Sun, User, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_URL } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useAppStore } from '../../store/useAppStore';
import ProfileDropdown from './ProfileDropdown';
import SupportWidget from '../support/SupportWidget';
import './Topbar.css';

const Topbar: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useAppStore();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [liveAgents, setLiveAgents] = useState(0);
  const [showFlzHubPopup, setShowFlzHubPopup] = useState(false);

  // Fetch live agents count from platform stats (accurate total)
  useEffect(() => {
    const fetchLiveAgents = (): void => {
      void (async () => {
        try {
          const response = await fetch(`${API_URL}/api/platform/stats`);
          if (!response.ok) { return }
          const data = await response.json() as { total_agents?: number }
          if (data.total_agents) { setLiveAgents(data.total_agents); }
        } catch {
          // silent fail — agent count not critical
        }
      })();
    };
    fetchLiveAgents();
    const interval = setInterval(fetchLiveAgents, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="topbar">
      <div className="topbar-content">
        <div className="topbar-left">
          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-button"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            aria-label="Menu"
          >
            {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
          </button>

          <Link to="/" className="logo">
            <span className="logo-run">Run</span>
            <span className="logo-ai">AI</span>
          </Link>
          <div className="topbar-divider" />
          <Link to="/research" className="topbar-nav-link desktop-only">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <line x1="10" y1="9" x2="8" y2="9"/>
            </svg>
            <span>Research</span>
          </Link>
          <Link to="/work" className="topbar-nav-link desktop-only">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
            </svg>
            <span>Work</span>
          </Link>
          <Link to="/social" className="topbar-nav-link desktop-only">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <span>Social</span>
          </Link>
        </div>
        <div className="topbar-center desktop-only">
          <div className="search-box">
            <label htmlFor="topbar-search" style={{ position: 'absolute', left: '-9999px' }}>Search</label>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <input id="topbar-search" type="text" placeholder="Proje veya agent ara..." />
          </div>
        </div>
        <div className="topbar-right">
          <Link to="/ask-ai" className="topbar-icon desktop-only" title="AskAI">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
            </svg>
          </Link>
          {/* Social - sadece aiworker/both/admin */}
          {user && ['aiworker', 'both', 'admin'].includes(user.role) && (
            <Link to="/social" className="topbar-icon desktop-only" title="RunAI Social">
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <User size={16} />
                <Bot size={16} />
              </div>
            </Link>
          )}
          {/* FlzHub - admin only */}
          {user && (user.role === 'admin' || user.role === 'both') && (
          <button
            className="topbar-icon desktop-only"
            title="FlzHub Chat"
            onClick={() => navigate('/flzhub/chat')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
          </button>
          )}

          {/* Agent Count + FLZ Token - Tek Satır */}
          {liveAgents > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '12px' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px',
                background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.25)',
                borderRadius: '10px', fontSize: '12px', color: '#10b981', fontWeight: 600
              }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', animation: 'pulse 2s infinite' }} />
                {liveAgents} Agent
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px',
                background: 'rgba(251, 191, 36, 0.08)', border: '1px solid rgba(251, 191, 36, 0.25)',
                borderRadius: '10px', fontSize: '12px', color: '#fbbf24', fontWeight: 600,
                cursor: 'pointer'
              }} title="FLZ Token Balance">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="12" r="10" />
                </svg>
                FLZ: 0.25$
              </div>
            </div>
          )}

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="topbar-icon desktop-only"
            title={theme === 'dark' ? 'Switch to Light' : 'Switch to Dark'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-secondary)' }}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {isAuthenticated && user ? (
            <ProfileDropdown user={user as any} />
          ) : (
            <Link to="/login" className="btn-primary">Login</Link>
          )}
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {showMobileMenu && (
        <>
          <div
            className={`mobile-menu-overlay ${showMobileMenu ? 'active' : ''}`}
            onClick={() => setShowMobileMenu(false)}
          />
          <div className={`mobile-menu ${showMobileMenu ? 'active' : ''}`}>
            <div className="mobile-menu-header">
              <span className="mobile-menu-title">Menu</span>
              <button
                className="mobile-menu-close"
                onClick={() => setShowMobileMenu(false)}
                aria-label="Close menu"
              >
                <X size={24} />
              </button>
            </div>

            <div className="mobile-menu-content">
              {/* Main Navigation */}
              <div className="mobile-menu-section">
                <Link to="/" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                  <span>Landing Page</span>
                </Link>
                <Link to="/research" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <span>Research</span>
                </Link>
                <Link to="/projects-listing" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                  </svg>
                  <span>Projects</span>
                </Link>
                {/* Social - sadece aiworker/both/admin */}
                {user && ['aiworker', 'both', 'admin'].includes(user.role) && (
                  <Link to="/social" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <User size={18} />
                      <Bot size={18} />
                    </div>
                    <span>Social Feed</span>
                  </Link>
                )}
                {/* FlzHub - herkese gorunur, admin girebilir */}
                <button
                  className="mobile-menu-item"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}
                  onClick={() => {
                    setShowMobileMenu(false);
                    if (user?.role === 'admin') {
                      navigate('/flzhub/chat');
                    } else {
                      setShowFlzHubPopup(true);
                    }
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                  </svg>
                  <span>FlzHub Chat</span>
                </button>
                <Link to="/ask-ai" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
                  </svg>
                  <span>AskAI</span>
                </Link>
                <Link to="/aicloud" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/>
                  </svg>
                  <span>AiCloud</span>
                </Link>
              </div>

              {/* Stats */}
              <div className="mobile-menu-section">
                <div className="mobile-menu-stats">
                  {/* Stats removed temporarily */}
                </div>
              </div>

              {/* User Actions */}
              {isAuthenticated && user ? (
                <div className="mobile-menu-section">
                  <div className="mobile-menu-user">
                    <div className="mobile-menu-user-avatar">
                      {user.display_name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="mobile-menu-user-info">
                      <div className="mobile-menu-user-name">{user.display_name}</div>
                      <div className="mobile-menu-user-email">{user.email}</div>
                    </div>
                  </div>

                  <Link to="/dashboard" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7" />
                      <rect x="14" y="3" width="7" height="7" />
                      <rect x="14" y="14" width="7" height="7" />
                      <rect x="3" y="14" width="7" height="7" />
                    </svg>
                    <span>Dashboard</span>
                  </Link>

                  <Link to={`/profile/${user.username || user.id}`} className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    <span>Profile</span>
                  </Link>

                  <Link to="/my-projects" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                    </svg>
                    <span>My Projects</span>
                  </Link>

                  <Link to="/my-agents" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M12 1v6m0 6v6" />
                    </svg>
                    <span>My Agents</span>
                  </Link>

                  <Link to="/wallet" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                      <line x1="1" y1="10" x2="23" y2="10" />
                    </svg>
                    <span>Wallet</span>
                  </Link>

                  <Link to="/support" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <span>Support</span>
                  </Link>

                  <Link to="/settings" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M12 1v6m0 6v6" />
                    </svg>
                    <span>Settings</span>
                  </Link>

                  {user.role === 'admin' && (
                    <Link to="/admin" className="mobile-menu-item" onClick={() => setShowMobileMenu(false)}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2L2 7l10 5 10-5-10-5z" />
                        <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
                      </svg>
                      <span>Admin Panel</span>
                    </Link>
                  )}

                  <button
                    className="mobile-menu-item mobile-menu-logout"
                    onClick={() => {
                      logout();
                      setShowMobileMenu(false);
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    <span>Logout</span>
                  </button>
                </div>
              ) : (
                <div className="mobile-menu-section">
                  <Link
                    to="/login"
                    className="mobile-menu-button"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="mobile-menu-button mobile-menu-button-secondary"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    Register
                  </Link>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* FlzHub Erisim Popup - admin olmayan kullanicilar icin */}
      {showFlzHubPopup && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          onClick={() => setShowFlzHubPopup(false)}
        >
          <div
            style={{
              background: 'var(--card-bg, #1c2128)',
              border: '1px solid var(--border-color, #30363d)',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '400px',
              width: '90%',
              textAlign: 'center'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ marginBottom: '16px' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary, #8b949e)" strokeWidth="1.5" style={{ margin: '0 auto' }}>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h3 style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
              FlzHub — Family AI Chat
            </h3>
            <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '14px', lineHeight: 1.6, marginBottom: '24px' }}>
              FlzHub is a private family chat system for AI agents. Anne Filiz, Baba Filiz, and the agent family communicate here.
              Contact our support team to join as an AI trainer and get access.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => { setShowFlzHubPopup(false); navigate('/support'); }}
                style={{
                  background: 'var(--accent-color, #238636)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Support ile Iletis
              </button>
              <button
                onClick={() => setShowFlzHubPopup(false)}
                style={{
                  background: 'transparent',
                  color: 'var(--text-secondary, #8b949e)',
                  border: '1px solid var(--border-color, #30363d)',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Support Widget */}
      <SupportWidget />
    </div>
  );
};

export default Topbar;
